/**
 * The feed contract: XTS message codes, the tick field set, and the binary
 * frame the engine publishes to browsers.
 *
 * In `shared` because three processes have to agree byte-for-byte — the engine
 * packs frames, the API relays them, the browser unpacks them into mirror
 * typed arrays. Two copies of a field order is a silently wrong price.
 */
// ─── XTS message codes ───────────────────────────────────────────────────────
/**
 * Subscriptions are per `(instrument, messageCode)` and the broker's cap counts
 * them that way ([B2] corrected). These are the codes that matter:
 *
 *   1501  TOUCHLINE     ltp, bid, ask, open/high/low/close, vwap
 *   1502  MARKET DEPTH  five-deep `Bids`/`Asks` AND a full `Touchline`.
 *         This is where an INDEX SPOT arrives on the measured gateway — as
 *         `Touchline.LastTradedPrice`, not as `IndexValue` ([B11]).
 *   1504  INDEX DATA    documented as `IndexValue` + its OHLC. Delivered
 *         NOTHING in a live 25 s window on the measured gateway.
 *   1510  OPEN INTEREST plus the underlying's total OI.
 *
 * Verified against a live socket, 2026-10-07 14:09 IST, 362 frames:
 * see `feed/replay/recordings/nifty-2026-10-07.json`.
 */
export const XTS_MESSAGE_CODES = {
    TOUCHLINE: 1501,
    INDEX_ALT: 1502,
    INDEX_DATA: 1504,
    OPEN_INTEREST: 1510,
};
/**
 * Warm price coverage per primary index: call/put strike pairs, per expiry.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY TWENTY-FOUR AND NOT FORTY — 9.32
 *
 * The cap counts SUBSCRIPTIONS, not instruments ([B2]): an option needs 1501
 * for its price AND 1510 for its open interest, so OI is literally the second
 * subscription per contract and is paid for out of the same budget.
 *
 * At forty, the warm reservation for the three primary indices was
 *
 *   3 x (40 strikes x 2 sides x 2 expiries + 2 index codes) = 486
 *
 * against a ceiling of 590. Production measured 559 of 590 in use, leaving
 * 31 free — and a single eighty-strike chain needs 160 for OI alone. Every
 * request was refused, so the chain rendered dashes in the OI column and
 * looked like a broker that does not send open interest. It was us, declining
 * to ask.
 *
 * Twenty-four brings the reservation to 3 x 98 = 294 and leaves roughly 296,
 * which fits a full visible chain's OI with room to spare.
 *
 * WHAT IT COSTS: warm price coverage is twenty-four strikes per expiry rather
 * than forty. A strike outside that band populates when it is first looked at
 * instead of already being there. Both expiries stay warm, which is the half
 * that was worth keeping — an expiry switch is a deliberate act, where a
 * missing OI column is a question about every row on screen.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const WARM_CHAIN_STRIKES = 24;
export const WARM_CHAIN_EXPIRIES = 2;
/** Same ATM-outward selection in the engine and browser, including an even strike count. */
export function nearestChainStrikes(contracts, atm, count = WARM_CHAIN_STRIKES) {
    const strikes = [...new Set(contracts.map((c) => c.strike))]
        .sort((a, b) => Math.abs(a - atm) - Math.abs(b - atm) || a - b)
        .slice(0, Math.max(0, count));
    const wanted = new Set(strikes);
    return contracts.filter((c) => wanted.has(c.strike));
}
/** Every code we ever bind a handler to. */
export const SUBSCRIBABLE_CODES = [
    XTS_MESSAGE_CODES.TOUCHLINE,
    XTS_MESSAGE_CODES.INDEX_ALT,
    XTS_MESSAGE_CODES.INDEX_DATA,
    XTS_MESSAGE_CODES.OPEN_INTEREST,
];
/**
 * The code sets, by what the instrument is and how closely it is being watched.
 *
 * This is where the subscription budget is actually spent ([B2] corrected).
 * OI is NOT always-on: 366 index-option instruments at 1501+1510 is 732
 * subscriptions against a cap of 500–600, while the same set at 1501 only is
 * 366. So OI is bought for the chain on screen and nowhere else.
 */
export const CODE_SETS = {
    /** Background option — prices only. */
    OPTION_PRICES: [XTS_MESSAGE_CODES.TOUCHLINE],
    /** Option on a chain someone is looking at — prices and open interest. */
    OPTION_FOREGROUND: [
        XTS_MESSAGE_CODES.TOUCHLINE,
        XTS_MESSAGE_CODES.OPEN_INTEREST,
    ],
    /**
     * Index spot — BOTH codes, deliberately, at a cost of 2 subscriptions.
     *
     * 1502 is the one that delivers here: measured live, it sent 250 frames in
     * 25 s for NIFTY 50 while 1504 sent none at all. 1504 is kept anyway because
     * it is the DOCUMENTED index event and the reference integration's own
     * gateway did publish `IndexValue` on it — and because the failure mode of
     * guessing wrong is a spot feed that is silent all session while looking
     * subscribed, which cost that desk a day. Five indices is ten subscriptions
     * against a ceiling of 460; this is not where the budget goes.
     */
    INDEX: [XTS_MESSAGE_CODES.INDEX_DATA, XTS_MESSAGE_CODES.INDEX_ALT],
    /**
     * An index spot where 1502 alone is known to deliver.
     *
     * Half the cost, and correct on the measured gateway. Not the default: a
     * provider has to be known to behave this way before it earns this, and
     * nothing records that yet.
     */
    INDEX_DEPTH_ONLY: [XTS_MESSAGE_CODES.INDEX_ALT],
    /** Future — touchline only; OI on a future is not rendered anywhere yet. */
    FUTURE: [XTS_MESSAGE_CODES.TOUCHLINE],
};
// ─── tiers ───────────────────────────────────────────────────────────────────
/**
 * Pool tiers (ARCHITECTURE.md §4.4). Ordered so that a numeric comparison is
 * an eviction-priority comparison: higher number dies first, and eviction
 * never crosses upward.
 */
export const TIER = {
    /** Open positions, armed strategy legs, working orders. NEVER evicted. */
    PINNED: 0,
    /** Always-on core: primary indices, near expiries, spots, futures. */
    CORE: 1,
    /** Refcounted demand — whatever is being viewed and isn't already core. */
    DEMAND: 2,
    /** Not subscribed. REST snapshot or poll only. */
    UNSUBSCRIBED: 3,
};
// ─── the tick field set ──────────────────────────────────────────────────────
/**
 * The fields a frame carries, in frame order.
 *
 * The ORDER IS THE WIRE FORMAT. Appending is safe (readers ignore trailing
 * fields they do not know, given `FRAME_VERSION`); reordering or removing is
 * not. Everything is a float64 so one typed array serves the lot and no field
 * needs a per-field scale factor — a price of 0.05 and an OI of 12,500,000 are
 * both exact.
 */
export const TICK_FIELDS = [
    'ltp',
    'bid',
    'ask',
    'open',
    'high',
    'low',
    'close',
    'vwap',
    'oi',
    /** Exchange stamp, ms since the Unix epoch, already normalised ([B13]). */
    'exchTs',
    /** When we received it, ms since the Unix epoch. */
    'recvTs',
];
export const TICK_FIELD_COUNT = TICK_FIELDS.length;
/** Field → index, for readers that want a name rather than a magic number. */
export const TICK_FIELD_INDEX = Object.freeze(Object.fromEntries(TICK_FIELDS.map((f, i) => [f, i])));
// ─── the published frame ─────────────────────────────────────────────────────
/**
 * Binary layout of a coalesced frame.
 *
 * ```
 *   offset  bytes  field
 *   0        2     magic           0xA1A0
 *   2        1     version         FRAME_VERSION
 *   3        1     shard
 *   4        4     count           number of instrument records
 *   8        8     flushedAt       float64, ms since the Unix epoch
 *   16       …     records
 *
 *   each record: 4 bytes token (int32) + TICK_FIELD_COUNT × 8 bytes float64
 * ```
 *
 * Little-endian throughout, because every platform this runs on is and
 * `DataView` byte-swapping per field on the browser's hot path is a real cost
 * for a portability nobody needs.
 *
 * A magic number and a version because this is the one format that crosses a
 * deploy boundary: a browser holding an old tab after a server deploy will be
 * handed frames by the new engine, and it has to recognise that rather than
 * decode garbage into prices.
 */
export const FRAME_MAGIC = 0xa1a0;
export const FRAME_VERSION = 1;
export const FRAME_HEADER_BYTES = 16;
export const FRAME_RECORD_BYTES = 4 + TICK_FIELD_COUNT * 8;
export const frameBytesFor = (count) => FRAME_HEADER_BYTES + count * FRAME_RECORD_BYTES;
/** How many records a buffer of this size can hold. Used to size the reusable buffer. */
export const frameCapacityFor = (bytes) => Math.max(0, Math.floor((bytes - FRAME_HEADER_BYTES) / FRAME_RECORD_BYTES));
// ─── staleness ───────────────────────────────────────────────────────────────
/**
 * Per-cell staleness thresholds, in ms since the exchange stamp.
 *
 * Rendered per cell rather than per chain (§4.7): with a shared pool at the
 * ceiling, an eviction freezes a price *someone else* is looking at, and the
 * chain as a whole looks perfectly healthy.
 *
 * `exchTs` is integer seconds at the source ([B13]), so one frame's lag is
 * quantised to 0–1 s of noise. FRESH therefore cannot be tighter than about a
 * second without flickering on a perfectly good feed.
 *
 * Measured live on 2026-10-07 against the three index spots: ages ran 236 ms
 * to 1,333 ms with all three reading FRESH, and SENSEX trailed NIFTY by
 * exactly 1,000 ms on some samples — which is the quantisation, not a slower
 * feed. One isolated 2,174 ms reading did tip SENSEX to LAGGING. So the 2 s
 * boundary has roughly 700 ms of margin on a healthy feed: enough, but tight
 * enough that ROADMAP 3.15 should set these against a real distribution rather
 * than inherit them.
 */
export const STALENESS_MS = {
    /** Within the quantisation noise of the exchange stamp. */
    FRESH: 2_000,
    /** Visibly behind. Dim the cell; do not hide it. */
    LAGGING: 6_000,
    /** Old enough that an order priced off it is a different trade. */
    STALE: 15_000,
};
export function stalenessOf(exchTs, now) {
    if (!(exchTs > 0))
        return 'DEAD';
    const age = now - exchTs;
    // A NEGATIVE age is a clock disagreement, not freshness. Returning FRESH for
    // it is how an uncorrected BSE stamp ([B13], 19,800 s ahead) renders every
    // SENSEX cell as live forever.
    if (age < -STALENESS_MS.FRESH)
        return 'DEAD';
    if (age <= STALENESS_MS.FRESH)
        return 'FRESH';
    if (age <= STALENESS_MS.LAGGING)
        return 'LAGGING';
    if (age <= STALENESS_MS.STALE)
        return 'STALE';
    return 'DEAD';
}
//# sourceMappingURL=feed.js.map